import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Comment, Ellipsis, Stack } from "@air/next-icons";
import { ClipType } from "@air/types";
import { formatBytes } from "@air/utilities";
import { getClipAspectRatio, isPdfRenderedExtension } from "@air/utils-clips";
import toUpper from "lodash/toUpper";
import pluralize from "pluralize";
import { memo, useCallback, useMemo } from "react";
import { CardAction } from "~/components/CardAction";
import { CustomFieldsDisplay } from "~/components/Gallery/CustomFieldsDisplay";
import { GalleryCardCustomFieldsAction } from "~/components/Gallery/GalleryCardCustomFieldsAction";
import { GalleryItemType } from "~/components/Gallery/types";
import { getAssetCustomFields } from "~/components/Gallery/utils";
import { GalleryAssetMenu } from "~/components/v2/components/shared/gallery/GalleryAssetMenu";
import { GalleryAssetShortInfo } from "~/components/v2/components/shared/gallery/GalleryAssetShortInfo";
import { GalleryAssetThumbnail } from "~/components/v2/components/shared/gallery/GalleryAssetThumbnail";
import { GalleryAssetTitle } from "~/components/v2/components/shared/gallery/GalleryAssetTitle";
import { GallerySelectableCard } from "~/components/v2/components/shared/gallery/GallerySelectableCard";
import { VideoScrubbableThumbnail } from "~/components/VideoScrubbableThumbnail";
import { useVideoScrubbableThumbnail } from "~/hooks/useVideoScrubbableThumbnail";
import { isTitleAndMetadataVisibleSelector, visibleCustomFieldsViewOptionsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
const _GalleryAsset = (param)=>{
    let { allCustomFields = [], canViewCustomFields, clip, getItemMenuOptions, getSelectionMenuOptions, height, isSelectable, onAssetClick, onAssetTitleCmdClick, onCustomFieldsClick, onDiscussionsClick, onVersionsClick, showVersionsIndicator, shouldShowDiscussions, width } = param;
    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } = useVideoScrubbableThumbnail({
        aspectRatio: getClipAspectRatio(clip),
        containerDimensions: {
            height,
            width
        }
    });
    const customFields = useMemo(()=>getAssetCustomFields({
            allCustomFields: allCustomFields || [],
            clip,
            visibleCustomFields
        }), [
        allCustomFields,
        clip,
        visibleCustomFields
    ]);
    const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;
    const clipDimensions = useMemo(()=>{
        const aspectRatio = clip.width / clip.height;
        return {
            width,
            height: width / aspectRatio
        };
    }, [
        width,
        clip.height,
        clip.width
    ]);
    const item = useMemo(()=>({
            id: clip.id,
            item: clip,
            type: GalleryItemType.asset
        }), [
        clip
    ]);
    const meta = useMemo(()=>{
        const meta = [];
        if (clip.ext) {
            meta.push(toUpper(clip.ext));
        }
        if (clip.size) {
            meta.push(formatBytes(clip.size));
        }
        if (!!clip.pages) {
            meta.push(pluralize("page", clip.pages, true));
        }
        if (!isPdfRenderedExtension(clip.ext) && !!clip.width && !!clip.height) {
            meta.push("".concat(clip.width, " x ").concat(clip.height));
        }
        return meta.join(" \xb7 ");
    }, [
        clip.ext,
        clip.height,
        clip.pages,
        clip.size,
        clip.width
    ]);
    const popoverCustomFields = useMemo(()=>{
        var _clip_customFields;
        return clip === null || clip === void 0 ? void 0 : (_clip_customFields = clip.customFields) === null || _clip_customFields === void 0 ? void 0 : _clip_customFields.filter((customField)=>{
            return allCustomFields.some((cf)=>cf.id === customField.id);
        });
    }, [
        clip,
        allCustomFields
    ]);
    const onTitleClick = useCallback((event)=>{
        if ((event.metaKey || event.ctrlKey) && !!onAssetTitleCmdClick) {
            onAssetTitleCmdClick === null || onAssetTitleCmdClick === void 0 ? void 0 : onAssetTitleCmdClick();
        } else {
            onAssetClick === null || onAssetClick === void 0 ? void 0 : onAssetClick();
        }
    }, [
        onAssetClick,
        onAssetTitleCmdClick
    ]);
    return /*#__PURE__*/ _jsx(GallerySelectableCard, {
        className: "flex size-full flex-col rounded-16 ring-2 ring-inset ring-transparent transition-colors data-[hovering=true]:bg-grey-4 data-[selected=true]:bg-grey-4 data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-blue-9",
        getItemMenuOptions: getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        isSelectable: isSelectable,
        item: item,
        onClick: onAssetClick,
        onMouseMove: onMouseMove,
        onMouseLeave: onMouseLeave,
        children: (param)=>{
            let { isHovering, setIsMenuOpen } = param;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none relative grow",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "absolute inset-1 flex overflow-hidden rounded-12",
                                children: clip.assets.spriteSheet && clip.type === ClipType.video && isHovering ? /*#__PURE__*/ _jsx(VideoScrubbableThumbnail, {
                                    backgroundImageRef: backgroundImageRef,
                                    indicatorRef: indicatorRef,
                                    src: clip.assets.spriteSheet,
                                    style: backgroundImageStyles
                                }) : /*#__PURE__*/ _jsx(GalleryAssetThumbnail, {
                                    clip: clip,
                                    isHovering: isHovering,
                                    width: clipDimensions.width,
                                    height: clipDimensions.height
                                })
                            }),
                            /*#__PURE__*/ _jsx(GalleryAssetShortInfo, {
                                clip: clip
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "absolute inset-1 flex flex-col justify-between overflow-hidden rounded-12",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between p-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    !!(popoverCustomFields === null || popoverCustomFields === void 0 ? void 0 : popoverCustomFields.length) && canViewCustomFields && /*#__PURE__*/ _jsx(GalleryCardCustomFieldsAction, {
                                                        customFields: popoverCustomFields,
                                                        onClick: onCustomFieldsClick
                                                    }),
                                                    showVersionsIndicator && !!onVersionsClick && /*#__PURE__*/ _jsx(CardAction, {
                                                        "data-testid": "CARD_VERSIONS_INDICATOR",
                                                        icon: /*#__PURE__*/ _jsx(Stack, {
                                                            className: "size-4"
                                                        }),
                                                        onClick: onVersionsClick
                                                    }),
                                                    shouldShowDiscussions && !!onDiscussionsClick && !!clip.openCommentCount && /*#__PURE__*/ _jsx(CardAction, {
                                                        "data-testid": "CARD_DISCUSSIONS_INDICATOR",
                                                        onClick: onDiscussionsClick,
                                                        icon: /*#__PURE__*/ _jsx(Comment, {
                                                            className: "size-4"
                                                        }),
                                                        text: clip.openCommentCount
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "pointer-events-auto flex items-center gap-1",
                                                children: isHovering && /*#__PURE__*/ _jsx(GalleryAssetMenu, {
                                                    clip: clip,
                                                    getItemMenuOptions: getItemMenuOptions,
                                                    getSelectionMenuOptions: getSelectionMenuOptions,
                                                    setIsMenuOpen: setIsMenuOpen,
                                                    trigger: /*#__PURE__*/ _jsx(CardAction, {
                                                        icon: /*#__PURE__*/ _jsx(Ellipsis, {
                                                            className: "size-4"
                                                        })
                                                    })
                                                })
                                            })
                                        ]
                                    }),
                                    !isTitleAndMetadataVisible && /*#__PURE__*/ _jsxs("div", {
                                        className: "flex min-h-[96px] flex-col items-stretch justify-end gap-0.5 bg-gradient-to-t from-black/85 to-transparent p-2 opacity-0 transition-opacity group-focus-within/gallery-selectable-card:opacity-100 group-data-[hovering=true]/gallery-selectable-card:opacity-100",
                                        children: [
                                            /*#__PURE__*/ _jsx(GalleryAssetTitle, {
                                                className: "text-white",
                                                clip: clip,
                                                onClick: onTitleClick,
                                                style: {
                                                    textShadow: "0px 0px 4px rgba(0, 0, 0, 0.40)"
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "truncate text-12 text-white",
                                                children: meta
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    (isTitleAndMetadataVisible || shouldShowCustomFields) && /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-none z-10 flex flex-col items-stretch gap-2 px-3 pb-2 pt-1",
                        children: [
                            isTitleAndMetadataVisible && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col items-stretch gap-0.5",
                                children: [
                                    /*#__PURE__*/ _jsx(GalleryAssetTitle, {
                                        clip: clip,
                                        onClick: onTitleClick
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "truncate text-12 text-grey-10",
                                        children: meta
                                    })
                                ]
                            }),
                            shouldShowCustomFields && /*#__PURE__*/ _jsx(CustomFieldsDisplay, {
                                customFields: customFields,
                                onClick: onCustomFieldsClick
                            })
                        ]
                    })
                ]
            });
        }
    });
};
export const GalleryAsset = /*#__PURE__*/ memo(_GalleryAsset);
